// @givmo/board — the Governance page's Meeting rules part: the numbers the organization recorded, and
// where each one comes from (D-CBG-9 made the former page one of the Governance page's two parts;
// its content and behavior are unchanged).
//
// This screen is the §3.2 structural cure made visible. The product computes notice arithmetic
// against these figures and echoes them back with their citation; it never reads the organization's bylaws
// and never states what they require. Everything on the page is phrased that way — "the notice period
// you recorded", never "your bylaws require" — and the terminology rail refuses the other phrasing.
//
// Recording one is Owner-only (`board_governance_parameter:manage`): it is an assertion the
// ORGANIZATION makes about its own instruments, in the class of terms acceptance, not of typing an
// agenda. A Member sees the values and the history, with an honest line about who can record them.

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { OrgBoardGovernanceParameterOut } from '@givmo/api-client';
import { Badge, Button, Card, CardBody, Skeleton } from '@givmo/design-system';
import { GovernancePartHeader } from '../governance/governance-part-header';
import { useBoardHost } from '../../board-host';
import { hasBoardPermission } from '../../board-host';
import { RecordParameterDialog } from './record-parameter-dialog';
import {
  approvalRule,
  displayDate,
  extractParameters,
  groupParameters,
  type ApprovalRule,
  type ParameterGroup,
} from './parameters-decode';
import { useBoardOperations } from '../../board-runtime';
import { HowThisWorks } from '../../components/fine-print';
import { minutesRuleWords as rc11 } from '../minutes/minutes-rule-words';
import { minutesSignaturesWords as rc10 } from '../minutes/minutes-signatures-words';

const selectParameters = (data: unknown): OrgBoardGovernanceParameterOut[] =>
  extractParameters(data);

/** The Meeting rules part of the Governance page. */
export function MeetingRulesPart() {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const { permissions, orgRef, isReady } = useBoardHost();
  const canRecord = hasBoardPermission(permissions, 'board_governance_parameter:manage');
  const [recording, setRecording] = useState(false);

  // With their earlier versions: each card's history is the rules the board recorded before.
  const parametersOptions = operations.officerListBoardGovernanceParametersOptions({
    path: { org_ref: orgRef ?? '' },
    query: { include_superseded: true },
  });
  const parametersQuery = useQuery({
    ...parametersOptions,
    enabled: isReady && orgRef !== null,
    select: selectParameters,
  });

  const groups = groupParameters(parametersQuery.data ?? []);
  // CBG-RC11: the board's two approval rules, each always shown (Givmo's starting point until one is
  // recorded), after the numbers.
  const minutesRule = approvalRule(parametersQuery.data ?? [], 'minutes_approval');
  const closedRule = approvalRule(parametersQuery.data ?? [], 'closed_session_minutes_approval');

  return (
    <div className="flex flex-col gap-24" data-testid="governance-meeting-rules">
      <GovernancePartHeader
        description={rc11.rulesDescription}
        actions={
          // Recording is `board_governance_parameter:manage`: without it, no button.
          canRecord ? (
            <Button
              appearance="saas"
              tone="primary"
              disabled={!isReady}
              onClick={() => setRecording(true)}
              testId="parameters-record"
            >
              Add a rule
            </Button>
          ) : undefined
        }
      />
      <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
        {/* Who enters a rule is said only to someone who can (`board_governance_parameter:manage`). */}
        {canRecord
          ? 'Givmo doesn’t read your governing documents. You enter each rule and where it’s written; Givmo does the counting with your numbers and shows where each one came from.'
          : 'Givmo doesn’t read your governing documents.'}
      </p>

      {parametersQuery.isPending && isReady ? (
        <div className="flex flex-col gap-12" data-testid="parameters-loading">
          <Skeleton className="h-84 w-full rounded-lg" />
          <Skeleton className="h-84 w-full rounded-lg" />
        </div>
      ) : null}

      {parametersQuery.isError ? (
        <Card testId="parameters-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              We couldn’t load the rules for this board.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void parametersQuery.refetch()}
              testId="parameters-error-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {parametersQuery.isSuccess &&
      groups.length === 0 &&
      minutesRule.active === null &&
      closedRule.active === null ? (
        // Nothing recorded: the approval rules' cards below still show Givmo's starting point, so the
        // numbers' empty state is one quiet line where their cards would be (CBG-RC11's review, P1).
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          data-testid="parameters-empty"
        >
          {canRecord
            ? 'Until you record a notice period, we won’t show a notice count on your meetings — there would be nothing to compare it against.'
            : 'Until a notice period is recorded, we won’t show a notice count on your meetings — there would be nothing to compare it against.'}
        </p>
      ) : null}

      {groups.map((group) => (
        <ParameterCard key={group.key} group={group} />
      ))}

      {parametersQuery.isSuccess ? (
        <>
          <ApprovalRuleCard
            rule={minutesRule}
            title={rc11.minutesRuleTitle}
            why={minutesWhy(minutesRule)}
            canRecord={canRecord}
            testId="parameters-card-minutes-approval"
          />
          <ApprovalRuleCard
            rule={closedRule}
            title={rc11.closedRuleTitle}
            why={
              closedRule.way === 'later_meeting'
                ? rc11.laterMeetingWhy
                : closedRule.way === 'directors_present'
                  ? // CBG-RC10 (frame g4): the way's own "When and why".
                    rc10.wayWhy(rc11.approvalRecordNotDecisions)
                  : rc11.closedSameWayWhy(minutesRule.way === 'written_consent')
            }
            canRecord={canRecord}
            testId="parameters-card-closed-minutes-approval"
          />
        </>
      ) : null}

      {recording && orgRef !== null ? (
        <RecordParameterDialog
          orgRef={orgRef}
          inForce={{
            minutes_approval: minutesRule.way,
            closed_session_minutes_approval: closedRule.way,
          }}
          onClose={() => setRecording(false)}
          onRecorded={() => {
            setRecording(false);
            void queryClient.invalidateQueries({ queryKey: parametersOptions.queryKey });
          }}
        />
      ) : null}
    </div>
  );
}

function ParameterCard({ group }: { group: ParameterGroup }) {
  const [showHistory, setShowHistory] = useState(false);
  const active = group.active;

  return (
    <Card testId={`parameters-card-${group.key}`}>
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-wrap items-baseline justify-between gap-12">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            {group.label}
          </h2>
          {active === null ? (
            <Badge tone="neutral">Not currently recorded</Badge>
          ) : (
            <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              Recorded {displayDate(active.recorded_at)}
            </span>
          )}
        </div>

        {active !== null ? (
          <>
            {/* The value is the organization's own words, echoed. Never re-rendered from the parts. */}
            <p
              className="font-sans text-s22 font-w700 tracking-givmo text-text-strong"
              data-testid={`parameters-value-${group.key}`}
            >
              {active.value_summary}
            </p>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              From your governing documents: {active.bylaw_citation}
            </p>
            {active.note ? (
              <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                {active.note}
              </p>
            ) : null}
          </>
        ) : (
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            Nothing is recorded for this today. Earlier versions are kept below.
          </p>
        )}

        {group.superseded.length > 0 ? (
          <div className="flex flex-col gap-8">
            <button
              type="button"
              onClick={() => setShowHistory((open) => !open)}
              className="self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              data-testid={`parameters-history-toggle-${group.key}`}
            >
              {showHistory
                ? 'Hide earlier versions'
                : `Show ${group.superseded.length} earlier ${group.superseded.length === 1 ? 'version' : 'versions'}`}
            </button>

            {showHistory ? (
              <ul
                className="flex flex-col gap-10 border-l border-border-subtle pl-16"
                data-testid={`parameters-history-${group.key}`}
              >
                {group.superseded.map((row) => (
                  <li key={row.id} className="flex flex-col gap-2">
                    <span className="font-sans text-s14 font-w600 tracking-givmo text-text-secondary">
                      {row.value_summary}
                    </span>
                    <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                      {row.bylaw_citation} · recorded {displayDate(row.recorded_at)} · replaced{' '}
                      {displayDate(row.superseded_at)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** The "Approving minutes" card's detail: the way in force, explained as the minutes page does. */
function minutesWhy(rule: ApprovalRule): readonly string[] {
  switch (rule.way) {
    case 'next_meeting':
      return rc11.nextMeetingWhy;
    case 'officer':
      return rc11.officerWhy;
    default:
      return rc11.writtenConsentWhy({ recusedNames: [], ruleRecorded: rule.active !== null });
  }
}

/**
 * CBG-RC11 (frames g1, g4) — one approval rule's card: the way in force in the card's words, where it
 * comes from (or Givmo's starting point, said as that), its detail behind "When and why", and its
 * earlier versions, the rule's history. The same card as a meeting rule's, with a way for a value.
 */
function ApprovalRuleCard({
  rule,
  title,
  why,
  canRecord,
  testId,
}: {
  rule: ApprovalRule;
  title: string;
  why: readonly string[];
  /** Who entering a rule is said to: only someone who can (`board_governance_parameter:manage`). */
  canRecord: boolean;
  testId: string;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const active = rule.active;
  const whenAndWhy = <HowThisWorks lines={why} label={rc11.whenAndWhy} testId={`${testId}-why`} />;
  return (
    <Card testId={testId}>
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-wrap items-baseline justify-between gap-12">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">{title}</h2>
          {active !== null ? (
            <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              Recorded {displayDate(active.recorded_at)}
            </span>
          ) : null}
        </div>
        <p
          className="font-sans text-s18 font-w700 leading-h15 tracking-givmo text-text-strong"
          data-testid={`${testId}-value`}
        >
          {rc11.wayLabel(rule.way)}
        </p>
        {active !== null ? (
          <>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {rc11.fromYourDocuments(active.bylaw_citation)}
            </p>
            {active.note ? (
              <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                {active.note}
              </p>
            ) : null}
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {whenAndWhy}
            </p>
          </>
        ) : (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            data-testid={`${testId}-default`}
          >
            {canRecord ? rc11.givmoStartsHere : rc11.givmoStartsHereReader} {whenAndWhy}
          </p>
        )}

        {rule.superseded.length > 0 ? (
          <div className="flex flex-col gap-8">
            <button
              type="button"
              onClick={() => setShowHistory((open) => !open)}
              className="self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              data-testid={`${testId}-history-toggle`}
            >
              {showHistory
                ? 'Hide earlier versions'
                : `Show ${rule.superseded.length} earlier ${rule.superseded.length === 1 ? 'version' : 'versions'}`}
            </button>
            {showHistory ? (
              <ul
                className="flex flex-col gap-10 border-l border-border-subtle pl-16"
                data-testid={`${testId}-history`}
              >
                {rule.superseded.map((row) => (
                  <li key={row.id} className="flex flex-col gap-2">
                    <span className="font-sans text-s14 font-w600 tracking-givmo text-text-secondary">
                      {rc11.wayLabel((row.choice_value ?? '').toLowerCase())}
                    </span>
                    <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                      {row.bylaw_citation} · recorded {displayDate(row.recorded_at)} · replaced{' '}
                      {displayDate(row.superseded_at)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
